import React, { useState, useEffect, useCallback } from 'react';

function BanquetIntelConfig({ token, selectedProperty, userScopes, accessibleProperties }) {
  const [config, setConfig] = useState({
    google_places_api_key: '',
    google_trends_enabled: false,
    trends_search_terms: '',
    trends_city: '',
    instagram_app_id: '',
    instagram_app_secret: '',
    facebook_access_token: '',
    sync_frequency: 'WEEKLY',
    is_enabled: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [showKeys, setShowKeys] = useState({});

  // Derive chain_id: prefer selectedProperty, fallback to scopes, fallback to first accessible property
  const chainId = selectedProperty?.chain_id
    || userScopes?.[0]?.chain_id
    || accessibleProperties?.[0]?.chain_id
    || '';

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': chainId,
    'x-property-id': 'GLOBAL',
  }), [token, chainId]);

  useEffect(() => {
    if (!chainId) return;
    setLoading(true);
    fetch('/api/v1/banquet-intelligence/config', { headers: headers() })
      .then(r => r.json())
      .then(data => setConfig(prev => ({ ...prev, ...data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [chainId, headers]);

  const handleSave = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      const res = await fetch('/api/v1/banquet-intelligence/config', {
        method: 'POST', headers: headers(), body: JSON.stringify(config),
      });
      const data = await res.json();
      if (res.ok) setMsg('Configuration saved successfully.');
      else setError(data.error || 'Failed to save');
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' };
  const sectionStyle = { marginBottom: '1.5rem', padding: '1rem 1.2rem', background: '#f8fafc', borderRadius: 10, border: '1px solid #e5e7eb' };

  const toggleKey = (key) => setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));

  const secretInput = (label, field, placeholder) => (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <input type={showKeys[field] ? 'text' : 'password'} value={config[field] || ''} onChange={e => setConfig({ ...config, [field]: e.target.value })} placeholder={placeholder} style={{ ...inputStyle, flex: 1 }} />
        <button onClick={() => toggleKey(field)} type="button" style={{ padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>
          {showKeys[field] ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );

  if (!chainId) return <div className="page-shell"><p style={{ color: '#94a3b8' }}>Unable to determine chain. Please log in again.</p></div>;

  return (
    <div className="page-shell" style={{ maxWidth: 720 }}>
      <h3 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.3rem' }}>Banquet Intelligence — API Configuration</h3>
      <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
        Configure 3rd party API keys for competitor data, Google Trends, and social media integration.
      </p>
      <div style={{ padding: '0.4rem 0.8rem', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', fontSize: '0.78rem', color: '#1e40af', marginBottom: '1.5rem', display: 'inline-block' }}>
        Global Setting — applies to all properties in this chain
      </div>

      {loading && <div style={{ color: '#6b7280', marginBottom: '1rem' }}>Loading...</div>}
      {msg && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#dcfce7', color: '#166534', fontSize: '0.85rem', marginBottom: '1rem' }}>{msg}</div>}
      {error && <div style={{ padding: '0.6rem 1rem', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: '0.85rem', marginBottom: '1rem' }}>{error}</div>}

      {/* Enable toggle */}
      <div style={{ ...sectionStyle, background: config.is_enabled ? '#f0fdf4' : '#f9fafb', borderColor: config.is_enabled ? '#bbf7d0' : '#e5e7eb' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
          <input type="checkbox" checked={config.is_enabled} onChange={e => setConfig({ ...config, is_enabled: e.target.checked })} style={{ width: 18, height: 18 }} />
          Enable Banquet Intelligence
        </label>
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 4 }}>
          {config.is_enabled ? 'Competitor tracking, demand forecast, and rate comparison are active.' : 'Feature is disabled.'}
        </div>
      </div>

      {/* Google Places API */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e40af', marginBottom: '0.75rem' }}>Google Places API</div>
        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
          Used to auto-fetch competitor ratings, reviews, and location data from Google Maps.
        </p>
        {secretInput('API Key', 'google_places_api_key', 'AIzaSy...')}
      </div>

      {/* Google Trends */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#059669', marginBottom: '0.75rem' }}>Google Trends</div>
        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
          Track search demand for banquet-related terms in your city. No API key needed — uses public data.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: '0.75rem' }}>
          <input type="checkbox" checked={config.google_trends_enabled} onChange={e => setConfig({ ...config, google_trends_enabled: e.target.checked })} />
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Enable Google Trends Tracking</span>
        </label>
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>City / Region</label>
          <input value={config.trends_city || ''} onChange={e => setConfig({ ...config, trends_city: e.target.value })} placeholder="e.g. Chennai" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Search Terms (one per line)</label>
          <textarea value={config.trends_search_terms || ''} onChange={e => setConfig({ ...config, trends_search_terms: e.target.value })}
            placeholder={"banquet hall in Chennai\nwedding venue Chennai\nconference hall Chennai"} rows={4}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.82rem' }} />
        </div>
      </div>

      {/* Social Media */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#7c3aed', marginBottom: '0.75rem' }}>Social Media (Instagram / Facebook)</div>
        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
          Track competitor social media activity — posts, engagement, event photos. Requires a Facebook App with Instagram Graph API access.
          These are your hotel's own Facebook Developer App credentials (not competitor credentials).
        </p>
        {secretInput('Facebook App ID (for Instagram Graph API)', 'instagram_app_id', 'App ID from Facebook Developer Console')}
        {secretInput('Facebook App Secret', 'instagram_app_secret', 'App Secret from Facebook Developer Console')}
        {secretInput('Facebook Page Access Token', 'facebook_access_token', 'Long-lived page access token (from your hotel\'s Facebook App)')}
        <div style={{ fontSize: '0.75rem', color: '#6b7280', background: '#f3f4f6', padding: '0.5rem 0.75rem', borderRadius: 6, marginTop: '0.5rem' }}>
          <strong>How to get these:</strong> Create a Facebook App at developers.facebook.com, enable Instagram Graph API,
          link your hotel's Facebook Page, and generate a Page Access Token. This token is used to discover competitor
          Instagram/Facebook data via Meta's Business Discovery API.
        </div>
      </div>

      {/* Sync Frequency */}
      <div style={sectionStyle}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#475569', marginBottom: '0.75rem' }}>Data Sync Schedule</div>
        <label style={labelStyle}>Auto-Sync Frequency</label>
        <select value={config.sync_frequency} onChange={e => setConfig({ ...config, sync_frequency: e.target.value })} style={{ ...inputStyle, maxWidth: 200 }}>
          <option value="DAILY">Daily</option>
          <option value="WEEKLY">Weekly</option>
          <option value="MANUAL">Manual Only</option>
        </select>
      </div>

      {/* Save */}
      <button onClick={handleSave} disabled={saving}
        style={{ padding: '0.6rem 1.5rem', borderRadius: 8, border: 'none', background: saving ? '#94a3b8' : 'var(--theme-primary-dark)', color: '#fff', fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: '0.9rem' }}>
        {saving ? 'Saving...' : 'Save Configuration'}
      </button>
    </div>
  );
}

export default BanquetIntelConfig;
